import { User } from "oidc-client-ts";
import { doDelete, doGet, doPost, doPut } from "./util/apiHelper.ts";
import {
  useAuthenticatedInfiniteQuery,
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { Weekday } from "../components/Weekdays.ts";
import { SUBSCRIPTION_QUERY_KEYS } from "./util/queryKeys.ts";

// -------------------------------------------------------------------------
// Types
// -------------------------------------------------------------------------

export interface SubscriptionRequestDto {
  teacherId: string;
  locationId: string;
  instrument: string;
  dayOfWeek: Weekday;
  timeZone: string;
  from: string; // LocalTime "HH:mm"
  to: string; // LocalTime "HH:mm"
}

export interface Subscription {
  id: string;
  subscriberId?: string;
  teacherId: string;
  locationId: string;
  instrument: string;
  dayOfWeek: Weekday;
  timeZone: string;
  from: string; // LocalTime "HH:mm:ss" (trim to HH:mm for display)
  to: string; // LocalTime "HH:mm:ss" (trim to HH:mm for display)
  price?: number;
  endingAt?: string; // LocalDate "yyyy-MM-dd"
  nextBillingAt?: string; // ISO instant string
  status?: "INCOMPLETE" | "ACCEPTED" | "ACTIVE" | "CANCELLED" | "PAST_DUE";
  cancellationReason?:
    | "TEACHER_ACCEPTANCE_TIMEOUT"
    | "PAYMENT_TIMEOUT"
    | "CANCELLED_BY_SUBSCRIBER"
    | "CANCELLED_BY_TEACHER";
  createdAt?: string; // ISO instant
  subscriberName?: string;
  subscriberProfilePictureUrl?: string;
  teacherName?: string;
  teacherProfilePictureUrl?: string;
  locationName?: string;
}

export interface SubscriptionResponseDto {
  subscriptions: Subscription[];
}

export interface CreateSubscriptionResponseDto {
  sessionSecret: string;
}

export interface SubscriptionPageDto {
  subscriptions: Subscription[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface AvailableSubscriptionSlot {
  dayOfWeek: Weekday;
  from: string; // LocalTime "HH:mm:ss"
  to: string; // LocalTime "HH:mm:ss"
  timeZone: string;
  conflictingBookingsCount: number;
}

export interface AvailableSubscriptionSlotsDto {
  slots: AvailableSubscriptionSlot[];
}

// -------------------------------------------------------------------------
// API functions
// -------------------------------------------------------------------------

async function createSubscription(
  user: User,
  subscriptionRequestDto: SubscriptionRequestDto,
): Promise<Subscription> {
  return await doPost<Subscription>(
    `/users/${user.profile.sub}/subscriptions`,
    user,
    subscriptionRequestDto,
  );
}

async function getUserSubscriptions(user: User): Promise<Subscription[]> {
  return (
    await doGet<SubscriptionResponseDto>(
      `/users/${user.profile.sub}/subscriptions`,
      user,
    )
  ).subscriptions;
}

async function getUserSubscription(
  user: User,
  subscriptionId: string,
): Promise<Subscription> {
  return await doGet<Subscription>(
    `/users/${user.profile.sub}/subscriptions/${subscriptionId}`,
    user,
  );
}

async function getTeacherSubscriptions(user: User): Promise<Subscription[]> {
  return (
    await doGet<SubscriptionResponseDto>(
      `/teachers/${user.profile.sub}/subscriptions`,
      user,
    )
  ).subscriptions;
}

async function getTeacherSubscriptionsByTeacherId(
  user: User,
  teacherId: string,
): Promise<Subscription[]> {
  return (
    await doGet<SubscriptionResponseDto>(
      `/teachers/${teacherId}/subscriptions`,
      user,
    )
  ).subscriptions;
}

async function acceptSubscription(
  user: User,
  subscriptionId: string,
): Promise<Subscription> {
  return await doPut<Subscription>(
    `/teachers/${user.profile.sub}/subscriptions/${subscriptionId}/accept`,
    user,
  );
}

async function cancelSubscription(
  user: User,
  subscriptionId: string,
): Promise<Subscription> {
  return await doDelete<Subscription>(
    `/users/${user.profile.sub}/subscriptions/${subscriptionId}`,
    user,
  );
}

const TEACHER_DASHBOARD_PAGE_SIZE = 10;

async function getTeacherSubscriptionsPaged(
  user: User,
  page: number,
  sortDir: "asc" | "desc",
): Promise<SubscriptionPageDto> {
  const params = new URLSearchParams({
    page: String(page),
    size: String(TEACHER_DASHBOARD_PAGE_SIZE),
    sortDir,
  });
  return doGet<SubscriptionPageDto>(
    `/teachers/${user.profile.sub}/subscriptions?${params.toString()}`,
    user,
  );
}

async function cancelTeacherSubscription(
  user: User,
  subscriptionId: string,
): Promise<Subscription> {
  return await doDelete<Subscription>(
    `/teachers/${user.profile.sub}/subscriptions/${subscriptionId}`,
    user,
  );
}

async function getAvailableSubscriptionSlots(
  user: User,
  teacherId: string,
  locationId: string,
  durationInMinutes: number,
): Promise<AvailableSubscriptionSlotsDto> {
  return await doGet<AvailableSubscriptionSlotsDto>(
    `/teachers/${teacherId}/subscriptions/available-slots?locationId=${locationId}&durationInMinutes=${durationInMinutes}`,
    user,
  );
}

async function finalizeSubscription(
  user: User,
  subscriptionId: string,
): Promise<CreateSubscriptionResponseDto> {
  return await doPut<CreateSubscriptionResponseDto>(
    `/users/${user.profile.sub}/subscriptions/${subscriptionId}`,
    user,
  );
}

// -------------------------------------------------------------------------
// Hooks
// -------------------------------------------------------------------------

export function useCreateSubscriptionMutation() {
  return useAuthenticatedMutation(
    (subscriptionRequestDto: SubscriptionRequestDto, user) => {
      return createSubscription(user, subscriptionRequestDto);
    },
    { invalidateKeys: [SUBSCRIPTION_QUERY_KEYS.userSubscriptions] },
  );
}

export function useUserSubscriptionsQuery() {
  return useAuthenticatedQuery(
    SUBSCRIPTION_QUERY_KEYS.userSubscriptions,
    (user) => getUserSubscriptions(user),
    { initialData: [] },
  );
}

export function useUserSubscriptionQuery(subscriptionId?: string) {
  return useAuthenticatedQuery(
    [...SUBSCRIPTION_QUERY_KEYS.userSubscription, subscriptionId],
    (user) => getUserSubscription(user, subscriptionId!),
    { enabled: !!subscriptionId },
  );
}

export function useTeacherSubscriptionsQuery() {
  return useAuthenticatedQuery(
    SUBSCRIPTION_QUERY_KEYS.teacherSubscriptions,
    (user) => getTeacherSubscriptions(user),
    { initialData: [] },
  );
}

export function useTeacherSubscriptionsByIdQuery(teacherId?: string) {
  return useAuthenticatedQuery(
    [...SUBSCRIPTION_QUERY_KEYS.teacherSubscriptionsById, teacherId],
    (user) => getTeacherSubscriptionsByTeacherId(user, teacherId!),
    { initialData: [], enabled: !!teacherId },
  );
}

export function useAvailableSubscriptionSlotsQuery(
  teacherId?: string,
  locationId?: string,
  durationInMinutes?: number,
) {
  return useAuthenticatedQuery(
    ["available-subscription-slots", teacherId, locationId, durationInMinutes],
    (user) =>
      getAvailableSubscriptionSlots(
        user,
        teacherId!,
        locationId!,
        durationInMinutes!,
      ),
    { enabled: !!teacherId && !!locationId && !!durationInMinutes },
  );
}

export function useAcceptSubscriptionMutation() {
  return useAuthenticatedMutation(
    (subscriptionId: string, user) => {
      return acceptSubscription(user, subscriptionId);
    },
    {
      invalidateKeys: [
        SUBSCRIPTION_QUERY_KEYS.teacherSubscriptions,
        SUBSCRIPTION_QUERY_KEYS.teacherSubscriptionsDashboard,
      ],
    },
  );
}

export function useCancelSubscriptionMutation() {
  return useAuthenticatedMutation(
    (subscriptionId: string, user) => {
      return cancelSubscription(user, subscriptionId);
    },
    {
      invalidateKeys: [
        SUBSCRIPTION_QUERY_KEYS.userSubscriptions,
        SUBSCRIPTION_QUERY_KEYS.userSubscription,
      ],
    },
  );
}

export function useTeacherSubscriptionsDashboardQuery(sortDir: "asc" | "desc") {
  return useAuthenticatedInfiniteQuery<SubscriptionPageDto>(
    [...SUBSCRIPTION_QUERY_KEYS.teacherSubscriptionsDashboard, sortDir],
    (user, page) => getTeacherSubscriptionsPaged(user, page, sortDir),
    (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
  );
}

export function useCancelTeacherSubscriptionMutation() {
  return useAuthenticatedMutation(
    (subscriptionId: string, user) => {
      return cancelTeacherSubscription(user, subscriptionId);
    },
    {
      invalidateKeys: [
        SUBSCRIPTION_QUERY_KEYS.teacherSubscriptions,
        SUBSCRIPTION_QUERY_KEYS.teacherSubscriptionsDashboard,
      ],
    },
  );
}

export function useFinalizeSubscriptionMutation() {
  return useAuthenticatedMutation(
    (subscriptionId: string, user) => {
      return finalizeSubscription(user, subscriptionId);
    },
    {
      invalidateKeys: [
        SUBSCRIPTION_QUERY_KEYS.userSubscriptions,
        SUBSCRIPTION_QUERY_KEYS.userSubscription,
      ],
    },
  );
}
